import React, { useMemo } from 'react';
import { MantineRadius, Select } from '@mantine/core';

import { Store } from '../../../codegen/graphql';
import { When } from '../../common';
import { ZoneStoreSelectPopover } from '../components/ZoneStoreSelectPopover';
import { requestComboboxSelectClassNames } from '../components/requestComboboxClassNames';
import { quickActionFormFieldFullWidthClassName } from './quickActionFormLayout';

type ApplicableTo = 'zone' | 'store';

type QuickActionFormStoreSelectionProps = {
    applicableTo: ApplicableTo;
    selectedStoreIds: string[];
    onSelectedStoreIdsChange: (selectedStoreIds: string[]) => void;
    selectedStoreIdsError?: string;
    eligibleZoneStores: Store[];
    highestCostPriceStoreId?: string | null;
    requestStore: Store | null;
    requestStoreName?: string | null;
    requestStoreNo?: string | null;
    fieldRadius?: MantineRadius;
};

function buildRequestStoreSelectData(
    requestStore: Store | null,
    requestStoreName?: string | null,
    requestStoreNo?: string | null,
): { value: string; label: string }[] {
    if (requestStore) {
        const label = requestStore.name
            ? `${requestStore.name}${
                  requestStore.storeNo ? ` (${requestStore.storeNo})` : ''
              }`
            : requestStore.storeNo ?? requestStore.id;

        return [{ value: requestStore.id, label }];
    }

    return [
        {
            value: '__current__',
            label: requestStoreName || requestStoreNo || 'Current store',
        },
    ];
}

export function QuickActionFormStoreSelection({
    applicableTo,
    selectedStoreIds,
    onSelectedStoreIdsChange,
    selectedStoreIdsError,
    eligibleZoneStores,
    highestCostPriceStoreId,
    requestStore,
    requestStoreName,
    requestStoreNo,
    fieldRadius = 'md',
}: QuickActionFormStoreSelectionProps) {
    const requestStoreSelectData = useMemo(
        () =>
            buildRequestStoreSelectData(
                requestStore,
                requestStoreName,
                requestStoreNo,
            ),
        [requestStore, requestStoreName, requestStoreNo],
    );

    const requestStoreSelectValue = requestStore?.id ?? '__current__';

    return (
        <>
            <When condition={applicableTo === 'zone'}>
                <div className={quickActionFormFieldFullWidthClassName}>
                    <ZoneStoreSelectPopover
                        eligibleZoneStores={eligibleZoneStores}
                        highestCostPriceStoreId={highestCostPriceStoreId}
                        selectedStoreIds={selectedStoreIds}
                        onChange={onSelectedStoreIdsChange}
                        radius={fieldRadius}
                        error={selectedStoreIdsError}
                    />
                </div>
            </When>

            <When condition={applicableTo === 'store'}>
                <div className={quickActionFormFieldFullWidthClassName}>
                    <Select
                        label="Store"
                        size="sm"
                        radius={fieldRadius}
                        readOnly
                        data={requestStoreSelectData}
                        value={requestStoreSelectValue}
                        comboboxProps={{
                            withinPortal: true,
                            zIndex: 1000,
                            width: 'target',
                        }}
                        classNames={requestComboboxSelectClassNames}
                        className="w-full min-w-0"
                    />
                </div>
            </When>
        </>
    );
}
